import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    ReactNode,
  } from "react";
  import { useFx } from "hooks/useFx";
  
  export type Currency = "USD" | "KES" | "EUR";

  export const SUPPORTED_CURRENCIES: Currency[] = ["USD", "KES", "EUR"];
  export const DEFAULT_CURRENCY: Currency = "USD";
  
  interface CurrencyCtx {
    currency: Currency;
    setCurrency: (c: Currency) => void;
    format: (usdAmount: number, opts?: Intl.NumberFormatOptions) => string;
    convert: (usdAmount: number) => number;
    isLoading: boolean;
    error: Error | null;
  }
  
  const SYMBOL: Record<Currency, string> = { USD: "$", KES: "Ksh", EUR: "€" };
  const DEFAULT_RATES: Record<Currency, number> = { USD: 1, KES: 130, EUR: 0.92 }; // Fallback rates
  
  const CurrencyContext = createContext<CurrencyCtx | undefined>(undefined);
  
  export const useCurrency = () => {
    const ctx = useContext(CurrencyContext);
    if (!ctx) throw new Error("useCurrency must be inside <CurrencyProvider />");
    return ctx;
  };
  
  export const CurrencyProvider = ({ children }: { children: ReactNode }) => {
    const [currency, setCurrency] = useState<Currency>(() => {
      const stored = localStorage.getItem("currency") as Currency | null;
      return stored && SUPPORTED_CURRENCIES.includes(stored) ? stored : DEFAULT_CURRENCY;
    });
  
    // Fetch exchange rates using useFx hook
    const { data: rates, isLoading, error } = useFx("USD");
  
    useEffect(() => {
      localStorage.setItem("currency", currency);
    }, [currency]);
  
    // Use fetched rates or fallback to DEFAULT_RATES if loading or error
    // Always use 130 for KES conversion rate (overrides API rate)
    const effectiveRates: Record<Currency, number> = rates
      ? { USD: rates.USD ?? 1, KES: 130, EUR: rates.EUR ?? DEFAULT_RATES.EUR }
      : DEFAULT_RATES;
  
    const convert = (usd: number) => +(usd * effectiveRates[currency]).toFixed(2);
  
    const format = (usd: number, opts: Intl.NumberFormatOptions = {}) =>
      new Intl.NumberFormat("en-KE", {
        style: "currency",
        currency: currency,
        currencyDisplay: "narrowSymbol",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        ...opts,
      }).format(convert(usd));
  
    return (
      <CurrencyContext.Provider
        value={{ currency, setCurrency, convert, format, isLoading, error }}
      >
        {children}
      </CurrencyContext.Provider>
    );
  };